import React, { Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'

// Suppress MetaMask connection errors (e.g. from extensions or Stripe wallet probe) so they don't show as app errors
const isMetaMaskError = (msg) =>
  typeof msg === 'string' && /MetaMask|Failed to connect to MetaMask/i.test(msg)
window.addEventListener('error', (event) => {
  if (isMetaMaskError(event.message) || isMetaMaskError(event.reason?.message || event.reason)) {
    event.preventDefault()
    event.stopPropagation()
    return true
  }
})
window.addEventListener('unhandledrejection', (event) => {
  if (isMetaMaskError(event.reason?.message || event.reason)) {
    event.preventDefault()
    event.stopPropagation()
  }
})

// After a deploy, stale tabs can request missing hashed chunks. Reload once
// per 10s window (avoids infinite reload if the chunk is still broken).
;(function () {
  const KEY = 'exituity_chunk_reload_at'
  const COOLDOWN_MS = 10000
  const isChunkError = (err) => {
    const name = err?.name || ''
    const msg = String(err?.message || err || '')
    return (
      name === 'ChunkLoadError' ||
      /Loading chunk [\w-]+ failed/i.test(msg) ||
      /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg)
    )
  }
  const recover = (err) => {
    if (!isChunkError(err)) return
    try {
      const now = Date.now()
      const last = Number(sessionStorage.getItem(KEY) || 0)
      if (now - last < COOLDOWN_MS) return
      sessionStorage.setItem(KEY, String(now))
      window.location.reload()
    } catch (_) {
      /* ignore */
    }
  }
  window.addEventListener('error', (event) => recover(event.error || event.message))
  window.addEventListener('unhandledrejection', (event) => recover(event.reason))
})()

// Send httpOnly session cookie (`exituity_rt`) on API fetches. JS cannot read that cookie;
// the browser only attaches it when credentials: 'include' and SameSite/CORS rules allow it.
;(function () {
  if (typeof window === 'undefined' || !window.fetch) return
  const orig = window.fetch.bind(window)
  const backendHost = (() => {
    const raw = process.env.REACT_APP_BACKEND_URL
    if (!raw || typeof raw !== 'string') return ''
    try {
      const u = raw.startsWith('http') ? raw : `https://${raw}`
      return new URL(u).host
    } catch {
      return ''
    }
  })()

  const needsCreds = (url) => {
    if (typeof url !== 'string') {
      try {
        return url && url.url && needsCreds(url.url)
      } catch (e) {
        return false
      }
    }
    // CRA dev server proxy: relative /api → backend (see package.json "proxy")
    if (url.startsWith('/api')) return true
    let parsed
    try {
      parsed = new URL(url, typeof window !== 'undefined' ? window.location.href : 'http://localhost')
    } catch {
      return false
    }
    if (!parsed.pathname.startsWith('/api')) return false
    if (backendHost && parsed.host === backendHost) return true
    if (parsed.hostname === 'api.exituity.com') return true
    if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') return true
    if (parsed.hostname.endsWith('.exituity.com')) return true
    return false
  }
  window.fetch = function (input, init) {
    const u = typeof input === 'string' ? input : input && input.url
    if (needsCreds(u)) {
      const next = { ...(init || {}) }
      if (next.credentials === undefined) next.credentials = 'include'
      return orig(input, next)
    }
    return orig(input, init)
  }
})()

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom'

import './design-system/index.css'
import './style.css'
import './components/Sidebar.css'
import './components/ChatWidget.css'
import './components/FloatingChatButton.css'
import './views/SellerDashboard.css'
import './views/SellerReviewMaterials.css'
// CustomerDashboard (Financial Dashboard) hidden — route redirects to /seller-dashboard
// import SaleProceeds from './views/SaleProceeds'
// import DiscoverAdvisors from './views/DiscoverAdvisors'
import ProtectedRoute from './components/ProtectedRoute'
import SessionSync from './components/SessionSync'
import { RolePreviewProvider } from './components/RolePreviewContext'
import { ProfileModalProvider } from './components/ProfileModalContext'
import { ProfilePhotoProvider } from './components/ProfilePhotoContext'
import { AccountSettingsProvider } from './components/AccountSettingsContext'
import { DebugProvider } from './components/DebugContext'
import Debugger from './components/Debugger'
import ReportIssueWidget from './components/ReportIssueWidget'

const Home = lazy(() => import('./views/home'))
const Pricing = lazy(() => import('./views/pricing'))
const Login = lazy(() => import('./views/login'))
const Signup = lazy(() => import('./views/signup'))
const DealAccess = lazy(() => import('./views/DealAccess'))
const ForgotPassword = lazy(() => import('./views/forgot-password'))
const ResetPassword = lazy(() => import('./views/reset-password'))
const AcceptInvite = lazy(() => import('./views/accept-invite'))
const VerifyEmail = lazy(() => import('./views/verify-email'))
const CustomerOnboardingPage = lazy(() => import('./views/customer-onboarding'))
const OnboardingQuestionnaire = lazy(() => import('./views/onboarding-questionnaire'))
const OnboardingRole = lazy(() => import('./views/onboarding-role'))
const BusinessProfileQuestionnaire = lazy(() => import('./views/business-profile-questionnaire'))
const EditBusinessPage = lazy(() => import('./views/edit-business'))
const ComparableCompanies = lazy(() => import('./views/ComparableCompanies'))
const VectorDBVisualization = lazy(() => import('./views/VectorDBVisualization'))
const Billing = lazy(() => import('./views/billing'))
const DeveloperApiPage = lazy(() => import('./views/DeveloperApiPage'))
const Payment = lazy(() => import('./views/payment'))
const HealthDashboard = lazy(() => import('./views/health-dashboard'))
const CIMOnboarding = lazy(() => import('./components/cim/CimOnboardingPage'))
const CimVoiceOnboarding = lazy(() => import('./views/cim-voice-onboarding'))
const DealRoom = lazy(() => import('./views/DealRoom'))
const FinancialScan = lazy(() => import('./views/FinancialScan'))
const ValuationBetaPage = lazy(() => import('./pages/valuation-beta'))

/** Preserve ?listingId= (and other query) when redirecting legacy /cim paths. */
function RedirectToCimOnboarding() {
  const location = useLocation()
  return <Navigate to={`/cim-onboarding${location.search || ''}`} replace />
}
const Messages = lazy(() => import('./views/Messages'))
const DealChecklist = lazy(() => import('./views/DealChecklist'))
const PreListing = lazy(() => import('./views/PreListing'))
const EngagementAgreement = lazy(() => import('./views/EngagementAgreement'))
const DealPreparation = lazy(() => import('./views/DealPreparation'))
const BrowseOpportunities = lazy(() => import('./views/BrowseOpportunities.js'))
const BusinessDetails = lazy(() => import('./views/BusinessDetails.js'))
const Team = lazy(() => import('./components/team'))
const Values = lazy(() => import('./components/values'))
const PrivacyPolicy = lazy(() => import('./views/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./views/TermsOfService'))
const BuyerNetwork = lazy(() => import('./components/buyer-network/BuyerNetwork'))
const WhoWeServe = lazy(() => import('./views/who-we-serve'))
const WhyChoose = lazy(() => import('./views/why-choose'))
const WhatExituityOffers = lazy(() => import('./views/what-exituity-offers'))
const Brand = lazy(() => import('./views/brand'))
const Blog = lazy(() => import('./views/blog'))
const BlogPost = lazy(() => import('./views/blog-post'))
const Alternatives = lazy(() => import('./views/alternatives'))
const Alternative = lazy(() => import('./views/alternative'))
const NotFound = lazy(() => import('./views/not-found'))
const Users = lazy(() => import('./views/users'))
const DiscoverListings = lazy(() => import('./views/DiscoverListings'))
const ServicesPage = lazy(() => import('./views/ServicesPage'))
const XtractPage = lazy(() => import('./views/Xtract'))
const RewardoLayout = lazy(() => import('./views/rewardo/RewardoLayout'))
const RewardoHome = lazy(() => import('./views/rewardo/Home'))
const RewardoPay = lazy(() => import('./views/rewardo/Pay'))
const RewardoWallet = lazy(() => import('./views/rewardo/Wallet'))
const RewardoSettings = lazy(() => import('./views/rewardo/Settings'))
const RewardoOnboarding = lazy(() => import('./views/rewardo/Onboarding'))
const FreeValuationPage = lazy(() => import('./views/FreeValuationPage'))
const CimAgentPage = lazy(() => import('./views/CimAgentPage'))
const GuestListingView = lazy(() => import('./views/GuestListingView'))
const SharedDealRoomView = lazy(() => import('./views/SharedDealRoomView'))
const BuyerDashboard = lazy(() => import('./views/BuyerDashboard'))
const BuyerOnboarding = lazy(() => import('./views/buyer-onboarding'))
const BuyerDealView = lazy(() => import('./views/BuyerDealView'))
const SellerOnboarding = lazy(() => import('./views/seller-onboarding'))
const OnboardingChat = lazy(() => import('./views/OnboardingChat'))
const SellerDashboard = lazy(() => import('./views/SellerDashboard.js'))
const AdvisorDashboard = lazy(() => import('./views/AdvisorDashboard.js'))
const SellerDealDetail = lazy(() => import('./views/SellerDealDetail.js'))
const SellerReviewMaterials = lazy(() => import('./views/SellerReviewMaterials'))
const RecommendedBuyers = lazy(() => import('./views/RecommendedBuyers.js'))
const Crm = lazy(() => import('./views/Crm'))
const DealSourcing = lazy(() => import('./views/DealSourcing'))
const SourcedBusinessProfile = lazy(() => import('./views/SourcedBusinessProfile'))
const AdvisorProfile = lazy(() => import('./views/AdvisorProfile'))

const PageFallback = () => <div className="page-loading" aria-live="polite" />

const CompareRedirect = () => {
  const { slug } = useParams()
  return <Navigate to={slug ? `/alternatives/${slug}` : '/alternatives'} replace />
}

const App = () => {
  return (
    <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <SessionSync />
      <RolePreviewProvider>
        <ProfileModalProvider>
        <ProfilePhotoProvider>
        <DebugProvider>
        <AccountSettingsProvider>
        <Suspense fallback={<PageFallback />}>
        <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Navigate to="/services/sellers" replace />} />
        <Route path="/services/brokers" element={<Navigate to="/services/sellers" replace />} />
        <Route path="/services/xtract" element={<Navigate to="/xtract" replace />} />
        <Route path="/services/cim-agent" element={<Navigate to="/cim-agent" replace />} />
        <Route path="/services/:personaSlug" element={<ServicesPage />} />
        <Route path="/xtract" element={<XtractPage />} />
        <Route path="/rewardo" element={<RewardoLayout />}>
          <Route index element={<RewardoHome />} />
          <Route path="pay" element={<RewardoPay />} />
          <Route path="wallet" element={<RewardoWallet />} />
          <Route path="settings" element={<RewardoSettings />} />
          <Route path="onboarding" element={<RewardoOnboarding />} />
          {/* Signed-out sign-up / log-in panel; the layout renders it directly. */}
          <Route path="access" element={null} />
        </Route>
        <Route path="/free-valuation" element={<FreeValuationPage />} />
        <Route path="/cim-agent" element={<CimAgentPage />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/discover-listings" element={<DiscoverListings />} />
        <Route path="/discover-listings/:listingId" element={<DiscoverListings />} />
        <Route path="/guest/listing/:token/:listingId" element={<GuestListingView />} />
        <Route path="/shared/deal-room/:token" element={<SharedDealRoomView />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/deal-access" element={<DealAccess />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        {/* Hidden for now */}
        <Route path="/valuation" element={<Navigate to="/seller-dashboard" replace />} />
        <Route path="/dashboard" element={<Navigate to="/seller-dashboard" replace />} />
        <Route path="/customer-dashboard" element={<Navigate to="/seller-dashboard" replace />} />
        <Route path="/onboarding-role" element={<ProtectedRoute><OnboardingRole /></ProtectedRoute>} />
        <Route path="/customer-onboarding" element={<ProtectedRoute><CustomerOnboardingPage /></ProtectedRoute>} />
        <Route path="/onboarding-questionnaire" element={<ProtectedRoute><OnboardingQuestionnaire /></ProtectedRoute>} />
        <Route path="/business-profile-questionnaire" element={<ProtectedRoute><BusinessProfileQuestionnaire /></ProtectedRoute>} />
        <Route path="/edit-business" element={<ProtectedRoute><EditBusinessPage /></ProtectedRoute>} />
        {/* Hidden for now */}
        <Route path="/dcf-analysis" element={<Navigate to="/seller-dashboard" replace />} />
        <Route path="/comparable-companies" element={<ProtectedRoute><ComparableCompanies /></ProtectedRoute>} />
        <Route path="/vector-db-visualization" element={<ProtectedRoute><VectorDBVisualization /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
        <Route path="/apis" element={<ProtectedRoute><DeveloperApiPage /></ProtectedRoute>} />
        <Route path="/payment" element={<ProtectedRoute><Payment /></ProtectedRoute>} />
        <Route path="/health" element={<ProtectedRoute><HealthDashboard /></ProtectedRoute>} />
        {/* Hidden for now */}
        {/* <Route path="/sale-proceeds" element={<ProtectedRoute><SaleProceeds /></ProtectedRoute>} /> */}
        <Route path="/valuation-calculator" element={<Navigate to="/customer-dashboard" replace />} />
        <Route path="/preview-customer-dashboard" element={<Navigate to="/seller-dashboard" replace />} />
        <Route path="/cim-onboarding" element={<ProtectedRoute><CIMOnboarding /></ProtectedRoute>} />
        <Route path="/cim-onboarding/voice" element={<ProtectedRoute><CimVoiceOnboarding /></ProtectedRoute>} />
        <Route path="/cim" element={<RedirectToCimOnboarding />} />
        <Route path="/deal-room" element={<ProtectedRoute><DealRoom /></ProtectedRoute>} />
        <Route path="/financial-scan" element={<ProtectedRoute><FinancialScan /></ProtectedRoute>} />
        <Route path="/valuation-beta" element={<ProtectedRoute><ValuationBetaPage /></ProtectedRoute>} />
        <Route path="/create-listing" element={<ProtectedRoute><PreListing /></ProtectedRoute>} />
        <Route path="/cim-review" element={<RedirectToCimOnboarding />} />
        <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        <Route path="/pre-listing" element={<Navigate to="/create-listing" replace />} />
        <Route path="/deal-checklist" element={<ProtectedRoute><DealChecklist /></ProtectedRoute>} />
        <Route path="/engagement-agreement" element={<ProtectedRoute><EngagementAgreement /></ProtectedRoute>} />
        <Route path="/deal-preparation" element={<ProtectedRoute><DealPreparation /></ProtectedRoute>} />
        <Route path="/financial-analysis-agent" element={<Navigate to="/deal-room" replace />} />
        <Route path="/deal-pipeline" element={<Navigate to="/deal-checklist" replace />} />
        <Route path="/buyer-pipeline" element={<Navigate to="/deal-checklist" replace />} />
        <Route path="/browse-opportunities" element={<ProtectedRoute><BrowseOpportunities /></ProtectedRoute>} />
        <Route path="/business-details/:businessId" element={<ProtectedRoute><BusinessDetails /></ProtectedRoute>} />
        <Route path="/team" element={<Team />} />
        <Route path="/values" element={<Values />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/privacy-policy.html" element={<Navigate to="/privacy-policy" replace />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/terms-of-service.html" element={<Navigate to="/terms-of-service" replace />} />
        <Route path="/culture" element={<Navigate to="/values" replace />} />
        <Route path="/buyer-network" element={<BuyerNetwork />} />
        <Route path="/for-buyers" element={<Navigate to="/buyer-network" replace />} />
        <Route path="/buyers" element={<Navigate to="/buyer-network" replace />} />
        <Route path="/who-we-serve" element={<WhoWeServe />} />
        <Route path="/why-choose" element={<WhyChoose />} />
        <Route path="/what-exituity-offers" element={<WhatExituityOffers />} />
        <Route path="/brand" element={<Brand />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/alternatives" element={<Alternatives />} />
        <Route path="/alternatives/:slug" element={<Alternative />} />
        <Route path="/compare" element={<Navigate to="/alternatives" replace />} />
        <Route path="/compare/:slug" element={<CompareRedirect />} />
        <Route path="/admin" element={<ProtectedRoute><Users /></ProtectedRoute>} />
        <Route path="/users" element={<Navigate to="/admin" replace />} />
        <Route path="/advisor-account" element={<Navigate to="/advisor-profile" replace />} />
        <Route path="/buyer-onboarding" element={<ProtectedRoute><BuyerOnboarding /></ProtectedRoute>} />
        <Route path="/buyer-dashboard" element={<ProtectedRoute><BuyerDashboard /></ProtectedRoute>} />
        <Route path="/listing" element={<ProtectedRoute><BuyerDealView /></ProtectedRoute>} />
        <Route path="/buyer-deal" element={<Navigate to="/listing" replace />} />
        <Route path="/seller-onboarding" element={<ProtectedRoute><SellerOnboarding /></ProtectedRoute>} />
        <Route path="/onboarding-chat" element={<ProtectedRoute><OnboardingChat /></ProtectedRoute>} />
        <Route path="/onboarding-chat/:listingId" element={<ProtectedRoute><OnboardingChat /></ProtectedRoute>} />
        <Route path="/seller-dashboard" element={<ProtectedRoute><SellerDashboard /></ProtectedRoute>} />
        <Route path="/advisor-dashboard" element={<ProtectedRoute><AdvisorDashboard /></ProtectedRoute>} />
        <Route path="/seller-deal/:listingId" element={<ProtectedRoute><SellerDealDetail /></ProtectedRoute>} />
        <Route path="/seller-review-materials" element={<ProtectedRoute><SellerReviewMaterials /></ProtectedRoute>} />
        <Route path="/deal-sourcing" element={<ProtectedRoute><DealSourcing /></ProtectedRoute>} />
        <Route path="/deal-sourcing/:businessId" element={<ProtectedRoute><SourcedBusinessProfile /></ProtectedRoute>} />
        <Route path="/manage-buyers" element={<Navigate to="/seller-dashboard" replace />} />
        <Route path="/seller-dashboard/buyers" element={<Navigate to="/seller-dashboard" replace />} />
        <Route path="/recommended-buyers" element={<ProtectedRoute><RecommendedBuyers /></ProtectedRoute>} />
        <Route path="/crm" element={<ProtectedRoute><Crm /></ProtectedRoute>} />
        <Route path="/contacts" element={<Navigate to="/crm" replace />} />
        <Route path="/campaigns" element={<Navigate to="/crm" replace />} />
        {/* Hidden for now */}
        {/* <Route path="/discover-advisors" element={<ProtectedRoute><DiscoverAdvisors /></ProtectedRoute>} /> */}
        <Route path="/advisor-profile" element={<ProtectedRoute><AdvisorProfile /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <Debugger />
        <ReportIssueWidget />
        </AccountSettingsProvider>
        </DebugProvider>
        </ProfilePhotoProvider>
        </ProfileModalProvider>
      </RolePreviewProvider>
    </Router>
  )
}

const container = document.getElementById('app')
const root = createRoot(container)
root.render(<App />)
